import React, { useState, useRef, useEffect } from "react";
import {
  BrainCircuit,
  X,
  Send,
  Loader2,
  Minimize2,
  Maximize2,
  TrendingUp,
  FileText,
  Calculator,
  AlertTriangle,
  BarChart3,
  Receipt,
} from "lucide-react";

/* OsirisBrain — Asistente Funcional de IA
   NO es un chatbot libre. Tiene funciones específicas contextuales
   que asisten al usuario en tareas concretas del ERP. */

interface Message {
  role: "user" | "assistant";
  content: string;
}

// Functional AI actions — context-specific assistance
const AI_FUNCTIONS = [
  {
    id: "dgii_606",
    icon: FileText,
    label: "Generar Reporte 606",
    prompt:
      "Genera un análisis del formato 606 de la DGII con mis compras y retenciones ITBIS del período actual. Dame un resumen de las retenciones aplicadas y las partidas que debo reportar.",
    color: "text-brand-500 bg-brand-50",
  },
  {
    id: "dgii_607",
    icon: Receipt,
    label: "Analizar 607 Ventas",
    prompt:
      "Analiza mis ventas para el formato 607 de la DGII. Revisa los NCF emitidos, las ventas con ITBIS y las exentas. Dame recomendaciones para el cierre del período.",
    color: "text-brand-500 bg-brand-50",
  },
  {
    id: "cash_flow",
    icon: TrendingUp,
    label: "Proyectar Flujo de Caja",
    prompt:
      "Basándote en mis cuentas por cobrar, cuentas por pagar y gastos recurrentes, proyecta mi flujo de caja para los próximos 30 días. Identifica fechas críticas de pago.",
    color: "text-brand-500 bg-brand-50",
  },
  {
    id: "tax_optimize",
    icon: Calculator,
    label: "Optimizar Carga Fiscal",
    prompt:
      "Analiza mi carga fiscal actual (ITBIS, ISR, retenciones) y sugiere estrategias legales para optimizar mi tributación en República Dominicana. Considera deducciones y créditos fiscales disponibles.",
    color: "text-brand-500 bg-brand-50",
  },
  {
    id: "overdue_alert",
    icon: AlertTriangle,
    label: "Alertas de Morosidad",
    prompt:
      "Revisa mis cuentas por cobrar y por pagar. Dame un reporte de facturas vencidas, clientes morosos, y proveedores a los que debo urgentemente. Prioriza por monto y antigüedad.",
    color: "text-red-600 bg-red-50",
  },
  {
    id: "kpi_summary",
    icon: BarChart3,
    label: "Resumen de KPIs",
    prompt:
      "Dame un resumen ejecutivo de mis indicadores clave: ventas del mes, margen bruto, rotación de inventario, días promedio de cobro, y comparación con el mes anterior. Identifica tendencias.",
    color: "text-brand-500 bg-brand-50",
  },
];

export const AiChat: React.FC<{ context?: string }> = ({ context }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content:
        "Soy OsirisBrain, tu asistente de IA integrado en el ERP. Selecciona una función para que te asista, o escribe una consulta específica sobre tu negocio.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);

    try {
      const response = await fetch("/api/ai-assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [
            ...messages.filter(
              (m) => m.role !== "assistant" || messages.indexOf(m) > 0,
            ),
            { role: "user", content: text },
          ],
          context: context,
        }),
      });
      const data = await response.json();
      if (data.error) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: `Error: ${data.error}` },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.reply },
        ]);
      }
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Error de conexión: ${err.message}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };
  const executeFunction = (fn: (typeof AI_FUNCTIONS)[0]) =>
    sendMessage(fn.prompt);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-ai-chat-trigger
        className="fixed bottom-6 right-6 w-14 h-14 bg-gray-900 hover:bg-gray-950 text-white rounded-full shadow-2xl flex items-center justify-center transition-all active:scale-90 z-40 group"
      >
        <BrainCircuit size={24} />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand-500 rounded-full border-2 border-white animate-pulse"></span>
      </button>
    );
  }

  return (
    <div
      className={`fixed ${expanded ? "inset-4" : "bottom-6 right-6 w-[420px] h-[650px]"} bg-white rounded-2xl shadow-2xl border border-brand-200 flex flex-col z-50 transition-all overflow-hidden`}
    >
      {/* Header */}
      <div className="p-4 border-b border-brand-100 bg-gray-900 text-white flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
            <BrainCircuit size={18} />
          </div>
          <div>
            <h3 className="text-sm font-bold">OsirisBrain — Asistente IA</h3>
            <p className="text-[10px] text-gray-400">
              Asistencia Funcional • DGII-aware
            </p>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button
            onClick={() => setOpen(false)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                msg.role === "user"
                  ? "bg-gray-900 text-white rounded-br-md"
                  : "bg-brand-100 text-gray-800 rounded-bl-md"
              }`}
            >
              <div className="whitespace-pre-wrap">{msg.content}</div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-brand-100 px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-2">
              <Loader2 size={14} className="animate-spin text-gray-500" />
              <span className="text-xs text-gray-500">
                OsirisBrain analizando...
              </span>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Functional Action Buttons (shown when few messages) */}
      {messages.length <= 2 && (
        <div className="px-3 pb-2">
          <p className="text-[10px] font-medium text-gray-500 uppercase mb-2 px-1">
            Funciones Disponibles
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {AI_FUNCTIONS.map((fn) => {
              const Icon = fn.icon;
              return (
                <button
                  key={fn.id}
                  onClick={() => executeFunction(fn)}
                  disabled={loading}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-left transition-all hover:scale-[1.02] active:scale-95 border border-brand-100 ${fn.color}`}
                >
                  <Icon size={14} className="shrink-0" />
                  <span className="text-[11px] font-bold leading-tight">
                    {fn.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        className="p-3 border-t border-brand-100 flex gap-2"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Consulta específica sobre tu negocio..."
          className="flex-1 bg-brand-50 border border-brand-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none"
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="w-10 h-10 bg-gray-900 hover:bg-gray-950 text-white rounded-xl flex items-center justify-center transition-colors disabled:opacity-30"
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  );
};

export default AiChat;
